import { PlusIcon } from '@shopify/polaris-icons';
import { Page, Modal, TextField } from '../components/forms';
import { useEffect, useRef, useState } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  DataTable,
  FormLayout,
  Checkbox,
  Banner,
  Spinner,
  SkeletonBodyText,
  DropZone,
  Divider,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../App';
import LabelPreview from '../components/labels/LabelPreview';

interface Location {
  id: string;
  code: string;
  zone: string;
  type: 'picking' | 'bulk' | 'receiving' | 'dispatch' | 'returns';
  barcode: string;
  stock_count: number;
}

interface Warehouse {
  id: string;
  name: string;
  address: string;
  shopify_location_id: string | null;
  is_default: number;
  location_count: number;
  total_stock: number;
  locations?: Location[];
}

export default function Warehouses() {
  const { fetchApi, error } = useApi();
  const {currentShop} = useAuth();
  const printGeneration = useRef(0);
  const [labelHtml, setLabelHtml] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState('');
  useEffect(() => {printGeneration.current++;setLabelHtml(null);setPrinting(false);return () => {printGeneration.current++;};}, [currentShop?.id]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [modalActive, setModalActive] = useState(false);
  const [locationModalActive, setLocationModalActive] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Warehouse form state
  const [formName, setFormName] = useState('');
  const [formAddress, setFormAddress] = useState('');
  const [formIsDefault, setFormIsDefault] = useState(false);

  // Location form state
  const [locCode, setLocCode] = useState('');
  const [locZone, setLocZone] = useState('');
  const locType = 'picking'; // Default type for new locations

  // Bulk location creation
  const [bulkPrefix, setBulkPrefix] = useState('');
  const [bulkAisles, setBulkAisles] = useState('1');
  const [bulkBays, setBulkBays] = useState('5');
  const [bulkShelves, setBulkShelves] = useState('4');

  // CSV import
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<Array<{ code: string; zone?: string; type?: string }>>([]);
  const [importing, setImporting] = useState(false);

  // Barcode printing
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

  useEffect(() => {
    loadWarehouses();
  }, []);

  async function loadWarehouses() {
    setLoading(true);
    try {
      const data = await fetchApi<{ warehouses: Warehouse[] }>('/inventory/warehouses');
      if (data?.warehouses) {
        setWarehouses(data.warehouses);
      }
    } finally {
      setLoading(false);
    }
  }

  async function loadWarehouseDetails(warehouseId: string) {
    const data = await fetchApi<{ warehouse: Warehouse; locations: Location[] }>(`/inventory/warehouses/${warehouseId}`);
    if (data) {
      setSelectedWarehouse({ ...data.warehouse, locations: data.locations });
    }
  }

  async function handleSyncFromShopify() {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const result = await fetchApi<{ success: boolean; imported: number; updated: number; total: number }>('/inventory/sync-from-shopify', {
        method: 'POST',
      });
      if (result?.success) {
        setSyncMessage({
          type: 'success',
          text: `Synced ${result.total} location(s) from Shopify (${result.imported} new, ${result.updated} updated)`,
        });
        loadWarehouses();
      } else {
        setSyncMessage({ type: 'error', text: 'Failed to sync from Shopify' });
      }
    } catch {
      setSyncMessage({ type: 'error', text: 'Failed to sync from Shopify' });
    }
    setSyncing(false);
  }

  const openNewWarehouseModal = () => {
    setSelectedWarehouse(null);
    setFormName('');
    setFormAddress('');
    setFormIsDefault(false);
    setModalActive(true);
  };

  const openEditWarehouseModal = (warehouse: Warehouse) => {
    setSelectedWarehouse(warehouse);
    setFormName(warehouse.name);
    setFormAddress(warehouse.address || '');
    setFormIsDefault(!!warehouse.is_default);
    setModalActive(true);
  };

  const handleSaveWarehouse = async () => {
    if (!formName.trim()) {
      return; // Don't submit without a name
    }

    const data = {
      name: formName.trim(),
      address: formAddress.trim(),
      is_default: formIsDefault,
    };

    let result;
    if (selectedWarehouse) {
      result = await fetchApi<{ success: boolean }>(`/inventory/warehouses/${selectedWarehouse.id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    } else {
      result = await fetchApi<{ success: boolean; id?: number }>('/inventory/warehouses', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    }

    if (result?.success) {
      setModalActive(false);
      loadWarehouses();
    }
    // If failed, modal stays open and error will be shown via the error state
  };

  const handleAddLocation = async () => {
    if (!selectedWarehouse) return;

    const result = await fetchApi<{success:boolean}>('/inventory/locations', {
      method: 'POST',
      body: JSON.stringify({
        warehouse_id: selectedWarehouse.id,
        code: locCode,
        zone: locZone,
        type: locType,
      }),
    });

    if (result?.success) {
      setLocCode('');
      setLocZone('');
      loadWarehouseDetails(selectedWarehouse.id);
      loadWarehouses();
    }
  };

  const handleBulkCreateLocations = async () => {
    if (!selectedWarehouse || !bulkPrefix) return;

    await fetchApi('/inventory/locations/bulk', {
      method: 'POST',
      body: JSON.stringify({
        warehouse_id: selectedWarehouse.id,
        zone: locZone || 'General',
        type: locType,
        prefix: bulkPrefix,
        aisles: parseInt(bulkAisles, 10),
        bays: parseInt(bulkBays, 10),
        shelves: parseInt(bulkShelves, 10),
      }),
    });

    setBulkPrefix('');
    setBulkAisles('1');
    setBulkBays('5');
    setBulkShelves('4');
    loadWarehouseDetails(selectedWarehouse.id);
  };

  // CSV Import handlers
  const handleCsvDrop = (_dropFiles: File[], acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setCsvFile(file);
      parseCsvFile(file);
    }
  };

  const parseCsvFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      const parsed: Array<{ code: string; zone?: string; type?: string }> = [];

      // Skip header if it looks like one (contains "code" or "location")
      const startIndex = lines[0]?.toLowerCase().match(/^(code|location)/) ? 1 : 0;

      for (let i = startIndex; i < lines.length; i++) {
        // Just take the first value (handles both single column and comma-separated)
        const code = lines[i].split(',')[0].trim().replace(/"/g, '');
        if (code) {
          parsed.push({ code });
        }
      }
      setCsvPreview(parsed);
    };
    reader.readAsText(file);
  };

  const handleCsvImport = async () => {
    if (!selectedWarehouse || csvPreview.length === 0) return;

    setImporting(true);
    const result = await fetchApi<{ success: boolean; imported: number; skipped: number }>('/inventory/locations/import-csv', {
      method: 'POST',
      body: JSON.stringify({
        warehouse_id: selectedWarehouse.id,
        locations: csvPreview,
      }),
    });

    if (result?.success) {
      setSyncMessage({
        type: 'success',
        text: `Imported ${result.imported} locations (${result.skipped} skipped)`,
      });
      setCsvFile(null);
      setCsvPreview([]);
      loadWarehouseDetails(selectedWarehouse.id);
    }
    setImporting(false);
  };

  // Barcode printing
  const handlePrintLabels = async () => {
    if (!selectedWarehouse?.locations?.length || printing) return;
    const locations = selectedLocations.length ? selectedWarehouse.locations.filter(location => selectedLocations.includes(location.id)) : selectedWarehouse.locations;
    if (locations.length > 100) {setPrintError('Select up to 100 locations at a time.');return;}
    setPrintError(''); setPrinting(true); const version = ++printGeneration.current;
    const result = await fetchApi<{html:string}>('/api/barcodes/location-labels', {method:'POST',body:JSON.stringify({warehouse_id:Number(selectedWarehouse.id),location_ids:locations.map(location => Number(location.id))})});
    if (version === printGeneration.current) {setPrinting(false);if (result) setLabelHtml(result.html);}
  };

  const toggleLocationSelection = (locId: string) => {
    setSelectedLocations(prev =>
      prev.includes(locId)
        ? prev.filter(id => id !== locId)
        : [...prev, locId]
    );
  };

  const selectAllLocations = () => {
    if (!selectedWarehouse?.locations) return;
    if (selectedLocations.length === selectedWarehouse.locations.length) {
      setSelectedLocations([]);
    } else {
      setSelectedLocations(selectedWarehouse.locations.map(l => l.id));
    }
  };

  const selectWarehouse = async (warehouse: Warehouse) => {
    printGeneration.current++;setSelectedLocations([]);setPrintError('');setLabelHtml(null);setPrinting(false);
    await loadWarehouseDetails(warehouse.id);
    setLocationModalActive(true);
  };

  return (
    <Page
      title="Warehouses"
      primaryAction={{
        icon: PlusIcon, content: 'Add Warehouse',
        onAction: openNewWarehouseModal,
      }}
      secondaryActions={[
        {
          content: syncing ? 'Syncing...' : 'Import from Shopify',
          onAction: handleSyncFromShopify,
          disabled: syncing,
        },
      ]}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {syncMessage && (
          <Layout.Section>
            <Banner
              tone={syncMessage.type === 'success' ? 'success' : 'critical'}
              onDismiss={() => setSyncMessage(null)}
            >
              {syncMessage.text}
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="p" tone="subdued">
                  Manage your warehouses and storage locations for inventory management.
                </Text>
                {syncing && <Spinner size="small" />}
              </InlineStack>

              {loading ? (
                <BlockStack gap="400">
                  <InlineStack align="center" gap="200">
                    <Spinner size="small" />
                    <Text as="p" tone="subdued">Loading warehouses...</Text>
                  </InlineStack>
                  <SkeletonBodyText lines={3} />
                </BlockStack>
              ) : warehouses.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'numeric', 'numeric', 'text', 'text']}
                  headings={['Name', 'Address', 'Locations', 'Stock', 'Shopify', 'Actions']}
                  rows={warehouses.map((wh) => [
                    <InlineStack gap="200" blockAlign="center">
                      <span>{wh.name}</span>
                      {wh.is_default ? <Badge tone="info">Default</Badge> : null}
                    </InlineStack>,
                    wh.address || '-',
                    (wh.location_count ?? 0).toString(),
                    (wh.total_stock ?? 0).toString(),
                    wh.shopify_location_id ? (
                      <Badge tone="success">Linked</Badge>
                    ) : (
                      <Badge tone="attention">Not Linked</Badge>
                    ),
                    <InlineStack gap="200">
                      <Button size="slim" onClick={() => selectWarehouse(wh)}>
                        Locations
                      </Button>
                      <Button size="slim" onClick={() => openEditWarehouseModal(wh)}>
                        Edit
                      </Button>
                    </InlineStack>,
                  ])}
                />
              ) : (
                <BlockStack gap="400" inlineAlign="center">
                  <Text as="p" tone="subdued">
                    No warehouses configured yet.
                  </Text>
                  <InlineStack gap="200">
                    <Button onClick={handleSyncFromShopify} disabled={syncing}>
                      Import from Shopify
                    </Button>
                    <Button onClick={openNewWarehouseModal} variant="plain">
                      Or add manually
                    </Button>
                  </InlineStack>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Add/Edit Warehouse Modal */}
      <Modal
        open={modalActive}
        onClose={() => setModalActive(false)}
        title={selectedWarehouse ? 'Edit Warehouse' : 'Add Warehouse'}
        primaryAction={{
          icon: selectedWarehouse ? undefined : PlusIcon, content: 'Save',
          onAction: handleSaveWarehouse,
          disabled: !formName.trim(),
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setModalActive(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {error && (
              <Banner tone="critical">{error}</Banner>
            )}
            <FormLayout>
              <TextField
                label="Warehouse Name"
                requiredIndicator value={formName}
                onChange={setFormName}
                autoComplete="off"

              />
              <TextField
                label="Address"
                value={formAddress}
                onChange={setFormAddress}
                multiline={3}
                autoComplete="off"
              />
              <Checkbox
                label="Set as default warehouse"
                checked={formIsDefault}
                onChange={setFormIsDefault}
              />
            </FormLayout>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {labelHtml && <LabelPreview html={labelHtml} title="Location label preview" filename="whenly-location-labels.html" onClose={() => setLabelHtml(null)}/> }
      {/* Locations Modal */}
      <Modal
        open={locationModalActive && !labelHtml}
        onClose={() => {
          printGeneration.current++;setPrinting(false);setLabelHtml(null);setPrintError('');
          setLocationModalActive(false);
          setCsvFile(null);
          setCsvPreview([]);
          setSelectedLocations([]);
        }}
        title={`Locations - ${selectedWarehouse?.name || ''}`}
        size="large"
      >
        <Modal.Section>
          <BlockStack gap="600">
            {/* Add Single Location */}
            <BlockStack gap="300">
              {error && <Banner tone="critical">{error}</Banner>}
              <Text as="h3" variant="headingMd">Add Location</Text>
              <InlineStack gap="400" blockAlign="end">
                <TextField
                  label="Code"
                  requiredIndicator value={locCode}
                  onChange={setLocCode}
                  placeholder="A-01-01"
                  autoComplete="off"
                />
                <TextField
                  label="Zone"
                  value={locZone}
                  onChange={setLocZone}
                  placeholder="Ambient, Chilled, etc."
                  autoComplete="off"
                />
                <Button icon={PlusIcon} onClick={handleAddLocation}>Add</Button>
              </InlineStack>
            </BlockStack>

            <Divider />

            {/* Bulk Create */}
            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">Bulk Create Locations</Text>
              <Text as="p" tone="subdued" variant="bodySm">
                Creates locations in format: PREFIX-AISLE-BAY-SHELF (e.g., A-01-03-02)
              </Text>
              <InlineStack gap="400" blockAlign="end">
                <TextField
                  label="Prefix"
                  value={bulkPrefix}
                  onChange={setBulkPrefix}
                  placeholder="A"
                  autoComplete="off"
                />
                <TextField
                  label="Aisles"
                  type="number"
                  requiredIndicator value={bulkAisles}
                  onChange={setBulkAisles}
                  autoComplete="off"
                />
                <TextField
                  label="Bays"
                  type="number"
                  requiredIndicator value={bulkBays}
                  onChange={setBulkBays}
                  autoComplete="off"
                />
                <TextField
                  label="Shelves"
                  type="number"
                  requiredIndicator value={bulkShelves}
                  onChange={setBulkShelves}
                  autoComplete="off"
                />
                <Button icon={PlusIcon} onClick={handleBulkCreateLocations}>
                  Create
                </Button>
              </InlineStack>
              <Text as="p" tone="subdued" variant="bodySm">
                Will create {parseInt(bulkAisles) * parseInt(bulkBays) * parseInt(bulkShelves)} locations
              </Text>
            </BlockStack>

            <Divider />

            {/* CSV Import */}
            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">Import from CSV</Text>
              <Text as="p" tone="subdued" variant="bodySm">
                One location code per line (e.g., BWN-3L1, BWN-3L2, etc.)
              </Text>
              <DropZone
                accept=".csv,text/csv"
                type="file"
                onDrop={handleCsvDrop}
                variableHeight
              >
                {csvFile ? (
                  <BlockStack gap="200" inlineAlign="center">
                    <Text as="p">{csvFile.name}</Text>
                    <Text as="p" tone="subdued">{csvPreview.length} locations to import</Text>
                  </BlockStack>
                ) : (
                  <DropZone.FileUpload actionHint="or drop CSV file to upload" />
                )}
              </DropZone>
              {csvPreview.length > 0 && (
                <BlockStack gap="200">
                  <Text as="p" variant="bodySm">Preview (first 5):</Text>
                  <Text as="p" tone="subdued" variant="bodySm">
                    {csvPreview.slice(0, 5).map(l => `${l.code} (${l.zone || 'General'})`).join(', ')}
                    {csvPreview.length > 5 && ` ... and ${csvPreview.length - 5} more`}
                  </Text>
                  <InlineStack gap="200">
                    <Button onClick={handleCsvImport} loading={importing} variant="primary">
                      {`Import ${csvPreview.length} Locations`}
                    </Button>
                    <Button onClick={() => { setCsvFile(null); setCsvPreview([]); }} variant="plain">
                      Cancel
                    </Button>
                  </InlineStack>
                </BlockStack>
              )}
            </BlockStack>

            <Divider />

            {/* Existing Locations */}
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h3" variant="headingMd">
                  Existing Locations ({selectedWarehouse?.locations?.length || 0})
                </Text>
                {(printError || error) && <Banner tone="critical">{printError || error}</Banner>}
                {selectedWarehouse?.locations && selectedWarehouse.locations.length > 0 && (
                  <InlineStack gap="200">
                    <Button size="slim" onClick={selectAllLocations}>
                      {selectedLocations.length === selectedWarehouse.locations.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    <Button
                      size="slim"
                      onClick={handlePrintLabels}
                      loading={printing}
                      disabled={selectedWarehouse.locations.length === 0}
                    >
                      {`Print Labels ${selectedLocations.length > 0 ? `(${selectedLocations.length})` : '(All)'}`}
                    </Button>
                  </InlineStack>
                )}
              </InlineStack>
              {selectedWarehouse?.locations && selectedWarehouse.locations.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'numeric']}
                  headings={['Select', 'Code', 'Zone', 'Type', 'Stock']}
                  rows={selectedWarehouse.locations.map((loc) => [
                    <Checkbox
                      label=""
                      labelHidden
                      checked={selectedLocations.includes(loc.id)}
                      onChange={() => toggleLocationSelection(loc.id)}
                    />,
                    loc.code,
                    loc.zone || '-',
                    loc.type,
                    (loc.stock_count ?? 0).toString(),
                  ])}
                />
              ) : (
                <Text as="p" tone="subdued">No locations yet. Add locations above, use bulk create, or import from CSV.</Text>
              )}
            </BlockStack>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
